import { Alert, Pagination, Skeleton, Snackbar } from "@mui/material";
import { MoreHorizontal, Power, PowerOff, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { checkTagValidity } from "../../Utils/Utils";
import {
  CHANGER_ETAT_MODE_PAIEMENT,
  MODE_PAIEMENTS,
  MODE_PAIMENTS_ENREGISTRER,
  SUPPRIMER_MODE_PAIEMENT,
} from "../../Utils/constant";
import cash from "../../assets/images/cash.png";
import moov from "../../assets/images/moov-money.png";
import momo from "../../assets/images/mtn-mooney.png";
import orange from "../../assets/images/om.png";
import visa from "../../assets/images/visa.png";
import wave from "../../assets/images/wave.png";
import model from "../../assets/model-fichier/mode_paiements.xlsx";
import { UploadButton } from "../../components/Widget/UploadButton";
import { deleteItem, find, store } from "../../services/service";

const Agence = () => {
  const [loading, setLoading] = useState(false);
  const [typeForm, setTypeForm] = useState("add");
  const [modePaiementId, setModePaiementId] = useState("");
  const [selectItem, setSelectItem] = useState(null);
  const [modePaiements, setModePaiement] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // États pour les menus et dialogs
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);

  // États pour les notifications
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const image = [
    {
      code: "WAVE_SKAN",
      image: wave,
    },
    {
      code: "OM_SKAN",
      image: orange,
    },
    {
      code: "MOOV_SKAN",
      image: moov,
    },
    {
      code: "MOMO",
      image: momo,
    },
    {
      code: "CARTE_BANCAIRE_CINETPAY_SKAN",
      image: visa,
    },
    {
      code: "CASH",
      image: cash,
    },
  ];

  const ModePaiments = (page, param, size) => {
    setLoading(true);
    find(
      `${MODE_PAIEMENTS}?page=${page ?? 0}&param=${param ?? ""}&size=${
        size ?? 10
      }`
    )
      .then((result) => {
        setModePaiement(result?.data?.donnee);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        showNotification("Erreur lors du chargement des données", "error");
      });
  };

  const more = (page) => {
    setCurrentPage(page);
    ModePaiments(page - 1, searchTerm);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    ModePaiments(0, value);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProvider(null);
  };

  const showNotification = (message, type = "success") => {
    setNotification({ open: true, message, type });
  };

  const handleStatusChange = async (provider, newStatus) => {
    setStatusUpdateLoading(true);
    try {
      await store(
        `${CHANGER_ETAT_MODE_PAIEMENT}?code=${provider.code}&typeEtat=${newStatus}`,
        "application/json",
        {}
      );

      showNotification(
        `Statut ${newStatus === "ACTIF" ? "activé" : "désactivé"} avec succès`,
        "success"
      );

      // Rafraîchir les données
      ModePaiments(currentPage - 1, searchTerm);
    } catch (error) {
      showNotification("Erreur lors de la mise à jour du statut", "error");
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectItem) return;

    setLoading(true);
    try {
      await deleteItem(`${SUPPRIMER_MODE_PAIEMENT}/${selectItem.id}`);
      showNotification("Moyen de paiement supprimé avec succès", "success");

      // Rafraîchir les données
      ModePaiments(currentPage - 1, searchTerm);

      setDeleteDialog(false);
      document.getElementById("delete").close();
    } catch (error) {
      showNotification("Erreur lors de la suppression", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (provider) => {
    setSelectItem(provider);
    setModePaiementId(provider.id);
    setTypeForm("edit");
    handleMenuClose();
  };

  useEffect(() => {
    ModePaiments();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Moyens de paiement
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Gérez vos méthodes de paiement acceptées
                </p>
              </div>

              {checkTagValidity("AJOUTER_AGENT") && (
                <UploadButton
                  uploadUrl={MODE_PAIMENTS_ENREGISTRER}
                  fetchData={() => {
                    ModePaiments(currentPage - 1, searchTerm);
                  }}
                  model={model}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Section */}
        <div className="mb-8">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              onChange={handleSearch}
              type="text"
              placeholder="Rechercher un moyen de paiement..."
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200"
              value={searchTerm}
            />
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {modePaiements?.data?.length === 0 ? (
            <div className="col-span-full">
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <Search className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucun résultat trouvé
                </h3>
                <p className="text-gray-500">
                  Essayez de modifier vos critères de recherche
                </p>
              </div>
            </div>
          ) : modePaiements?.data?.length !== 0 && !loading ? (
            modePaiements?.data?.map((provider) => (
              <div
                key={provider.id}
                className={`bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 group overflow-hidden ${
                  provider.typeEtat !== "ACTIF" ? "opacity-60" : ""
                }`}
              >
                <div className="p-6">
                  {/* Header avec menu */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden">
                          <img
                            className="w-8 h-8 object-contain"
                            src={
                              image.find((logo) => logo.code === provider.code)
                                ?.image
                            }
                            alt={provider.designation}
                          />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-sm">
                            {provider.designation}
                          </h3>
                          <p className="text-xs text-gray-500 capitalize">
                            {provider?.typeOperateur
                              ?.replace(/_/g, " ")
                              .toLowerCase()}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu d'actions */}
                    <div className="dropdown dropdown-end">
                      <div
                        tabIndex={0}
                        role="button"
                        className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </div>
                      <ul className="dropdown-content z-10 menu p-2 shadow-xl bg-white rounded-xl border border-gray-200 w-48 mt-2">
                        <li>
                          <button
                            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-200"
                            onClick={() =>
                              handleStatusChange(
                                provider,
                                provider.typeEtat === "ACTIF"
                                  ? "INACTIF"
                                  : "ACTIF"
                              )
                            }
                            disabled={statusUpdateLoading}
                          >
                            {provider.typeEtat === "ACTIF" ? (
                              <PowerOff className="h-4 w-4 text-red-500" />
                            ) : (
                              <Power className="h-4 w-4 text-green-500" />
                            )}
                            {provider.typeEtat === "ACTIF"
                              ? "Désactiver"
                              : "Activer"}
                          </button>
                        </li>
                        <li>
                          <button
                            onClick={() => {
                              setSelectItem(provider);
                              document.getElementById("delete").showModal();
                            }}
                            className="flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          >
                            <Trash2 className="h-4 w-4" />
                            Supprimer
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center justify-center">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        provider.typeEtat === "ACTIF"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      <div
                        className={`w-2 h-2 rounded-full mr-2 ${
                          provider.typeEtat === "ACTIF"
                            ? "bg-green-500"
                            : "bg-red-500"
                        }`}
                      />
                      {provider.typeEtat === "ACTIF" ? "Actif" : "Inactif"}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // Loading skeletons
            Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <Skeleton
                      variant="rectangular"
                      width={48}
                      height={48}
                      className="rounded-lg"
                    />
                    <div>
                      <Skeleton width={120} height={16} className="mb-1" />
                      <Skeleton width={80} height={12} />
                    </div>
                  </div>
                  <Skeleton
                    variant="rectangular"
                    width={32}
                    height={32}
                    className="rounded-lg"
                  />
                </div>
                <div className="flex justify-center">
                  <Skeleton width={80} height={24} className="rounded-full" />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {modePaiements?.totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <Pagination
              onChange={(event, newValue) => more(newValue)}
              count={modePaiements?.totalPages}
              page={currentPage}
              variant="outlined"
              color="primary"
              shape="rounded"
              size="large"
            />
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <dialog id="delete" className="modal">
        <div className="modal-box max-w-md bg-white rounded-2xl shadow-2xl border-0">
          <div className="text-center p-6">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <Trash2 className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Confirmer la suppression
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Êtes-vous sûr de vouloir supprimer{" "}
              <span className="font-medium text-gray-900">
                {selectItem?.designation}
              </span>{" "}
              ? Cette action est définitive.
            </p>
            <div className="flex items-center justify-center gap-4">
              <form method="dialog">
                <button className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-all duration-200">
                  Annuler
                </button>
              </form>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 min-w-[120px] justify-center"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Suppression...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Supprimer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>close</button>
        </form>
      </dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.type}
          variant="filled"
          className="shadow-lg"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Agence;
